/**
 * Wires `soldWorks.model.ts` to the ledger's read and its one write.
 *
 * Calls `fetchGalleryPlacements` and `useWorkThumbnails` — a request of its
 * own rather than a filter over the inventory, because a sale ends the
 * consignment and a sold work lives in a different relation — and
 * `withdrawTransfer` to unsay an open sale, followed by a re-read since a
 * withdrawal also voids the settlements the sale raised.
 *
 * The state the model cannot hold is the chosen artist, which row is armed
 * for the two-press withdrawal, which row is being written, and the reload
 * counter. Arming a second row disarms the first.
 */

import { useEffect, useState } from "react";
import type { PlacedWork } from "@ondo/shared";

import { fetchGalleryPlacements, withdrawTransfer } from "../lib/api.ts";
import { useWorkThumbnails } from "../lib/useWorkThumbnails.ts";
import { describeSoldWorksError, toSoldWorksView } from "./soldWorks.model.ts";
import type { SoldWorksView } from "./soldWorks.model.ts";

export interface SoldWorksController {
  view: SoldWorksView;
  /** Narrow the list to one artist. "" is all of them. */
  chooseArtist: (artist: string) => void;
  /** First press. Arms the confirmation on one row and disarms any other. */
  askToWithdraw: (transferId: string) => void;
  /** Changed their mind. */
  cancelWithdraw: () => void;
  /** Second press. Ends the sale and re-reads the ledger. */
  withdraw: (transferId: string) => void;
  /** Which row is armed, or null. */
  confirmingId: string | null;
  /** The row being written, so its buttons can go quiet. */
  working: string | null;
}

/**
 * The gallery's ledger of what it has placed.
 *
 * ONE READ AND ONE WRITE, and the write took a while to arrive because of what
 * this comment used to say: "withdrawing is a correction that wants a
 * confirmation this screen has nowhere to put". The confirmation is now here,
 * and it is the same two-press shape `deleteRoom` uses — the first press arms
 * one row, the second acts, and arming a second row disarms the first.
 *
 * `ondo.withdraw_transfer()` has existed since the transfers migration and had
 * no caller anywhere in the app, so a gallery that recorded a sale to the wrong
 * buyer, or recorded one that fell through, had no way to unsay it.
 *
 * IT IS NOT AN ERASURE. The row stays and its status becomes `withdrawn`, any
 * settlements it raised are voided rather than deleted, and only a sale still
 * OPEN can be withdrawn at all — `accepted` is refused, because once title has
 * landed with a buyer, undoing it is a new transfer back.
 *
 * Accepting is still somebody else's act: the buyer answers, and nothing here
 * can answer for them.
 *
 * A SECOND REQUEST rather than a filter over the inventory, and that is the
 * shape of the domain rather than a routing decision — see
 * `fetchGalleryPlacements`. A sale ENDS the consignment, so a sold work is not a
 * differently-flagged inventory row; it is a different table reached through a
 * different relation, and one screen being empty says nothing about the other.
 *
 * @param workBase '/gallery/ONDO-6624/work' — where a row links to.
 */
export function useSoldWorksController(workBase: string): SoldWorksController {
  const [placements, setPlacements] = useState<PlacedWork[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const images = useWorkThumbnails(placements, (each) => ({
    id: each.workId,
    imagePath: each.imagePath,
  }));
  const [artist, setArtist] = useState("");
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let active = true;

    fetchGalleryPlacements()
      .then((found) => {
        if (active) setPlacements(found);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeSoldWorksError(cause));
      });

    return () => {
      active = false;
    };
    // `nonce` re-reads after a withdrawal. The ledger is the only copy of what
    // is true; incrementing it is how this screen says so.
  }, [nonce]);

  return {
    view: toSoldWorksView({ placements, error, images, workBase, artist }),
    chooseArtist: setArtist,
    confirmingId,
    working,

    askToWithdraw: (transferId) => {
      setError(null);
      setConfirmingId(transferId);
    },

    cancelWithdraw: () => setConfirmingId(null),

    // RE-READ rather than mutate a local copy, the rule `useSettlementsController`
    // states: withdrawing voids any settlements the sale raised, and a browser
    // holding a second opinion about which of those are live is a browser that
    // will eventually be wrong about money.
    withdraw: (transferId) => {
      setWorking(transferId);
      setError(null);

      withdrawTransfer(transferId)
        .then(() => {
          setConfirmingId(null);
          setNonce((current) => current + 1);
        })
        .catch((cause: unknown) => setError(describeSoldWorksError(cause)))
        .finally(() => setWorking(null));
    },
  };
}
